import { useCallback, useMemo, useRef, useState } from "react";
import cx from "classnames";

import { TableBodyCell } from "../../../base/cells/TableBodyCell";
import { DataTableBodyCellProps as BaseDataTableBodyCellProps } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableCells.module.scss";
import baseTableCellStyles from "../../../base/cells/TableCells.module.scss";
import { Cell } from "@tanstack/react-table";
import { useCommonPinningStyles } from "../../../internal/getCommonPinningStyles";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { CellPosition } from "../../../internal/types";
import {
  INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID,
  INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID,
} from "../constants";
import { TableRow } from "../../../types";
import { CellFocusContext } from "./CellFocusContext";

/**
 * Props for the DataTableBodyImmutableCell component
 */
export type DataTableBodyImmutableCellProps<T> = Omit<
  BaseDataTableBodyCellProps,
  "onChange"
> & {
  /**
   * The position of the cell in the table
   */
  cellPosition: CellPosition;
  /**
   * The TanStack table cell instance
   */
  cell: Cell<T, unknown>;
  /**
   * Whether the cell is currently hovered
   */
  isHovered: boolean;
};

/**
 * Renders an immutable (non-editable) body cell in a DataTable. This component handles keyboard
 * navigation for focus management and row expansion/selection, but does not support cell editing.
 *
 * @param props - The props for the DataTableBodyImmutableCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to render in the cell
 * @param props.isHovered - Whether the cell is currently hovered
 * @returns The rendered immutable cell component
 */
export function DataTableBodyImmutableCell<T>(
  props: DataTableBodyImmutableCellProps<T>,
) {
  const { cell, cellPosition, children, isHovered, ...rest } = props;

  const cellRef = useRef<HTMLTableCellElement>(null);
  const [tabIndex, setTabIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const { moveFocusToCell } = useDTFocusDispatchContext();

  const column = cell.column;

  const rowCanExpand = cell.row.getCanExpand();
  const rowIsExpanded = cell.row.getIsExpanded();
  const toggleRowExpanded = cell.row.toggleExpanded;

  // Derive cell action from column type and row state
  const columnId = column.id;
  const isPrimaryActionColumn =
    columnId === INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID;
  const isSecondaryActionColumn =
    columnId === INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID;
  const isNestedRow = !!cell.row.getParentRow()?.getCanExpand();

  const isExpandCell = isPrimaryActionColumn && rowCanExpand;
  const isSelectCell =
    (isPrimaryActionColumn && isNestedRow && cell.row.getCanSelect()) ||
    (isSecondaryActionColumn && !isNestedRow);

  const meta = (cell.row.original as TableRow<T>).meta;
  const cellError = meta?.errors?.[column.id as keyof T];
  const cellWarning = meta?.warnings?.[column.id as keyof T];

  const classes = cx(styles["data-table-body-cell"], rest.className, {
    [styles["data-table-body-cell-hovered"]]: isHovered,
    [styles["data-table-body-cell-error"]]: !!cellError,
    [styles["data-table-body-cell-warning"]]: !cellError && !!cellWarning,
    [baseTableCellStyles["table-cell-internal-expand"]]: isExpandCell,
    [baseTableCellStyles["table-cell-internal-select"]]: isSelectCell,
  });
  const commonPinningStyles = useCommonPinningStyles<T>(column);

  const { jumpFocus, moveFocus } = useDTFocusDispatchContext();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveFocus("up");
          return;
        case "ArrowDown":
          event.preventDefault();
          moveFocus("down");
          return;
        case "ArrowLeft":
          event.preventDefault();
          if (rowCanExpand && rowIsExpanded) {
            toggleRowExpanded();
            return;
          }
          moveFocus("left");
          return;
        case "ArrowRight":
          event.preventDefault();
          if (rowCanExpand && !rowIsExpanded) {
            toggleRowExpanded();
            return;
          }
          moveFocus("right");
          return;
        case "Home": {
          event.preventDefault();
          jumpFocus("left");
          return;
        }
        case "End": {
          event.preventDefault();
          jumpFocus("right");
          return;
        }
        case "Enter":
        case " ": {
          if (isExpandCell || isSelectCell) {
            event.preventDefault();
            if (isExpandCell) {
              toggleRowExpanded();
            }
            if (isSelectCell) {
              cell.row.toggleSelected();
            }
            return;
          }
          return;
        }
        default:
          break;
      }
    },
    [
      moveFocus,
      jumpFocus,
      isExpandCell,
      isSelectCell,
      cell,
      rowCanExpand,
      rowIsExpanded,
      toggleRowExpanded,
    ],
  );

  const handleFocus = useCallback(() => {
    setTabIndex(0);
    setIsFocused(true);
    moveFocusToCell(cellPosition);
  }, [moveFocusToCell, cellPosition]);

  const handleBlur = useCallback(() => {
    setTabIndex(-1);
    setIsFocused(false);
  }, []);

  const cellFocusContextValue = useMemo(
    () => ({ isCellFocused: isFocused }),
    [isFocused],
  );

  return (
    <TableBodyCell
      type="data-table"
      {...rest}
      className={classes}
      ref={cellRef}
      tabIndex={tabIndex}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={commonPinningStyles}
      onKeyDown={handleKeyDown}
      data-cell-pos={formatCellPositionString(cellPosition)}
    >
      <CellFocusContext.Provider value={cellFocusContextValue}>
        {children}
      </CellFocusContext.Provider>
    </TableBodyCell>
  );
}
